<!-- eslint-disable vuejs-accessibility/label-has-for -->
<template>
  <div
    ref="root"
    :class="classes"
    :style="styles"
  >
    <div
      class="input-container accessibility-outline outline-inset"
      tabindex="0"
      :tabindex="disabled ? -1 : 0"
      :aria-disabled="disabled"
      :aria-required="required"
      :aria-readonly="isReadOnly"
      :aria-describedby="inputAriaDescribedby"
      :aria-invalid="hasError"
      :aria-label="label || 'Auswahl'"
      :aria-placeholder="placeholderText"
      :aria-autocomplete="isSearchable ? 'list' : undefined"
      :aria-controls="listboxId"
      :aria-expanded="listActive && !disabled ? 'true' : 'false'"
      @keydown.enter="[
        !listActive && hasItems && multiple ? toggleList() : '',
        openFileCondition ? $refs.file.click() : '',
        isSearchable ? $refs.input.focus() : '',
      ]"
      @keydown.space.prevent="[
        !listActive && hasItems && multiple ? toggleList() : '',
        openFileCondition ? $refs.file.click() : '',
        isSearchable ? $refs.input.focus() : '',
      ]"
    >
      <div class="input-container-inner">
        <div
          v-if="hasSlotIconLeft"
          class="icon-left-slot accessibility-outline outline-inset"
          role="button"
          tabindex="0"
          :tabindex="disabled ? -1 : 0"
          :aria-label="toggleLabel"
          :aria-expanded="hasList ? (listActive && !disabled ? 'true' : 'false') : undefined"
          :aria-controls="hasList ? listboxId : undefined"
          :aria-describedby="inputAriaDescribedby || undefined"
          :aria-disabled="disabled"
          @click="[
            !listActive && hasItems && multiple ? toggleList() : '',
            openFileCondition ? $refs.file.click() : '',
            isSearchable ? $refs.input.focus() : '',
          ]"
        >
          <slot name="iconLeft" />
        </div>
        <div
          v-if="iconLeft && !hasSlotIconLeft"
          class="icon-left accessibility-outline outline-inset"
          role="button"
          tabindex="0"
          :tabindex="disabled ? -1 : 0"
          :aria-label="toggleLabel"
          :aria-expanded="hasList ? (listActive && !disabled ? 'true' : 'false') : undefined"
          :aria-controls="hasList ? listboxId : undefined"
          :aria-describedby="inputAriaDescribedby || undefined"
          :aria-disabled="disabled"
          @click="[
            !listActive && hasItems && multiple ? toggleList() : '',
            openFileCondition ? $refs.file.click() : '',
            isSearchable ? $refs.input.focus() : '',
          ]"
          @keydown.enter.stop.prevent="[
            !listActive && hasItems && multiple ? toggleList() : '',
            openFileCondition ? $refs.file.click() : '',
            isSearchable ? $refs.input.focus() : '',
          ]"
          @keydown.space.stop.prevent="[
            !listActive && hasItems && multiple ? toggleList() : '',
            openFileCondition ? $refs.file.click() : '',
            isSearchable ? $refs.input.focus() : '',
          ]"
        >
          <BaseBadge
            filled="company-main-primary"
            pill
            padding-large
            :size="badgeSize"
          >
            <span :key="iconLeft">
              <i :class="iconLeft" />
            </span>
          </BaseBadge>
        </div>
        <div
          :id="buttonId"
          ref="inputField"
          class="input-field accessibility-outline outline-inset"
          role="button"
          tabindex="0"
          :tabindex="disabled ? -1 : 0"
          :aria-label="toggleLabel"
          :aria-expanded="hasList ? (listActive && !disabled ? 'true' : 'false') : undefined"
          :aria-controls="hasList ? listboxId : undefined"
          :aria-describedby="inputAriaDescribedby || undefined"
          :aria-invalid="hasError ? 'true' : 'false'"
          :aria-required="required"
          :aria-disabled="disabled"
          @click="[
            !listActive && hasItems && multiple ? toggleList() : '',
            openFileCondition ? $refs.file.click() : '',
            isSearchable ? $refs.input.focus() : '',
          ]"
          @keydown.enter.stop.prevent="[
            !listActive && hasItems && multiple ? toggleList() : '',
            openFileCondition ? $refs.file.click() : '',
            isSearchable ? $refs.input.focus() : '',
          ]"
          @keydown.space.stop.prevent="[
            !listActive && hasItems && multiple ? toggleList() : '',
            openFileCondition ? $refs.file.click() : '',
            isSearchable ? $refs.input.focus() : '',
          ]"
        >
          <label :for="isSearchable ? baseFieldId : name">{{ label }}</label>
          <div
            v-if="isSearchable ? hasItemSelected && multiple : multiple"
            ref="selectedList"
            :class="['input-selected-list']"
          >
            <span
              v-for="(selection, inx) in internalValue"
              ref="selectedListItem"
              :key="`${selection}-${inx}`"
              class="input-selected-list--item"
            >
              {{ getItem(selection) ? getItem(selection).label : selection }}{{ isList && inx !== internalValue.length - 1 ? ',&nbsp;' : '' }}
              <span
                v-if="!isList && hasItems"
                class="input-selected-list--item-icon accessibility-outline outline-inset"
                role="button"
                tabindex="0"
                :tabindex="disabled ? -1 : 0"
                :aria-label="getRemoveLabel(selection)"
                @click.stop="selectItem(getItem(selection), internalValue)"
                @keydown.enter.stop.prevent="selectItem(getItem(selection), internalValue)"
                @keydown.space.stop.prevent="selectItem(getItem(selection), internalValue)"
              >
                <span aria-hidden="true"><i class="fa-thin fa-times" /></span>
              </span>
            </span>
          </div>
          <div
            v-else-if="isSearchable ? hasItemSelected : true"
            class="input-selected-item"
          >
            <span
              v-if="hasValue"
              ref="selectedListItem"
            >{{ getItem(internalValue).label }}</span>
          </div>
          <input
            v-if="isSearchable"
            ref="input"
            v-model="search"
            class="accessibility-outline outline-inset"
            :name="name"
            :placeholder="placeholderText"
            :autocomplete="autocomplete"
            aria-autocomplete="list"
            :aria-controls="hasList ? listboxId : undefined"
            :aria-expanded="hasList ? (listActive && !disabled ? 'true' : 'false') : undefined"
            :aria-describedby="inputAriaDescribedby || undefined"
            :aria-disabled="disabled"
            @input="listActive = true"
            @keydown="onKeyDown($event, { list: internalValue })"
            @focusin="focused = true"
            @focusout="onFocusOut($event)"
          >
          <input
            :id="name"
            ref="file"
            type="file"
            class="input-field--hidden"
            :accept="accept"
            :capture="capture"
            :multiple="multiple"
            :aria-label="label || 'Datei auswählen'"
            :aria-describedby="inputAriaDescribedby || undefined"
            :aria-required="required"
            :aria-invalid="hasError"
            tabindex="-1"
            @input="selectFile($event.target.files); resetFileInput()"
          >
        </div>
        <div
          v-if="hasSlotIconRight"
          class="icon-right-slot accessibility-outline outline-inset"
          role="button"
          tabindex="0"
          :tabindex="[
            clearCondition || disabled ? -1 : 0,
            openFileCondition ? 0 : -1,
            isSearchable ? 0 : -1,
          ]"
          :aria-label="iconRightLabel"
          :aria-expanded="hasList && !clearCondition ? (listActive && !disabled ? 'true' : 'false') : undefined"
          :aria-controls="hasList && !clearCondition ? listboxId : undefined"
          :aria-disabled="disabled"
          @click="[
            clearCondition ? clear($event, { val: multiple ? [] : '' }) : '',
            !listActive && !hasValue && hasItems && multiple ? toggleList() : '',
            openFileOnIconCondition ? $refs.file.click() : '',
            isSearchable ? $refs.input.focus() : '',
          ]"
          @keydown.enter.stop.prevent="[
            clearCondition ? clear($event, { val: multiple ? [] : '' }) : '',
            !listActive && !hasValue && hasItems && multiple ? toggleList() : '',
            openFileOnIconCondition ? $refs.file.click() : '',
            isSearchable ? $refs.input.focus() : '',
          ]"
          @keydown.space.stop.prevent="[
            clearCondition ? clear($event, { val: multiple ? [] : '' }) : '',
            !listActive && !hasValue && hasItems && multiple ? toggleList() : '',
            openFileOnIconCondition ? $refs.file.click() : '',
            isSearchable ? $refs.input.focus() : '',
          ]"
        >
          <slot name="iconRight" />
        </div>
        <div
          v-if="!hasSlotIconRight"
          class="icon-right accessibility-outline outline-inset"
          role="button"
          tabindex="0"
          :tabindex="disabled ? -1 : 0"
          :aria-label="iconRightLabel"
          :aria-expanded="hasList && !clearCondition ? (listActive && !disabled ? 'true' : 'false') : undefined"
          :aria-controls="hasList && !clearCondition ? listboxId : undefined"
          :aria-disabled="disabled"
          @click="[
            clearCondition ? clear($event, { val: multiple ? [] : '' }) : '',
            !listActive && !hasValue && hasItems && multiple ? toggleList() : '',
            openFileOnIconCondition ? $refs.file.click() : '',
            isSearchable ? $refs.input.focus() : '',
          ]"
          @keydown.enter.stop.prevent="[
            clearCondition ? clear($event, { val: multiple ? [] : '' }) : '',
            !listActive && !hasValue && hasItems && multiple ? toggleList() : '',
            openFileOnIconCondition ? $refs.file.click() : '',
            isSearchable ? $refs.input.focus() : '',
          ]"
          @keydown.space.stop.prevent="[
            clearCondition ? clear($event, { val: multiple ? [] : '' }) : '',
            !listActive && !hasValue && hasItems && multiple ? toggleList() : '',
            openFileOnIconCondition ? $refs.file.click() : '',
            isSearchable ? $refs.input.focus() : '',
          ]"
        >
          <span v-if="clearCondition" aria-hidden="true">
            <span><i class="fa-thin fa-times" /></span>
          </span>
          <span v-else-if="!hasValue && getIconRight" aria-hidden="true">
            <span><i :class="getIconRight" /></span>
          </span>
          <span v-else-if="defaultIcon" aria-hidden="true">
            <span><i :class="defaultIcon" /></span>
          </span>
        </div>
      </div>
      <BaseItemList
        :value="internalValue"
        :list-active="listActive && multiple && !disabled"
        :items="selectableItems"
        :multiple="multiple"
        :icon-left="iconLeft"
        :visible-items="visibleItems"
        :list-id="listboxId"
        :labelled-by="buttonId"
        @select="[setSelection($event), !multiple || focused ? toggleList() : '']"
        @close="toggleList()"
      />
    </div>
    <div
      v-if="hasError || hint || counter"
      class="input-hint"
    >
      <div
        v-if="hasError"
        :id="errorMessageId"
        class="input-hint--error"
        role="alert"
        aria-live="assertive"
        v-html="errorMessage"
      />
      <div
        v-if="hint"
        :id="hintId"
        class="input-hint--hint"
        aria-live="polite"
      >
        <span v-if="accept">
          {{ typeof hint === 'boolean' ? `Erlaubte Dateiformate: ${accept}` : hint }}
        </span>
        <span v-if="maxSize && (typeof hint === 'boolean' || !hint)">Maximale Dateigröße: {{ maxSize }} KB
        </span>
      </div>
      <div
        v-if="counter"
        :id="counterId"
        class="input-hint--counter"
      >
        <span>{{ internalValue.length }} {{ max > 0 ? ` | ${max}` : '' }}</span>
      </div>
    </div>
  </div>
</template>

<script>
import BaseSelectMixin from '../../../shared/mixins/BaseSelectMixin';

import BaseItemList from './BaseItemList.vue';

export default {
  name: 'BaseFileInput',
  components: {
    BaseItemList,
  },
  mixins: [
    BaseSelectMixin,
  ],
  props: {
    value: {
      type: [String, Array, File, FileList],
      required: false,
      default: () => null,
    },
    accept: {
      type: String,
      required: false,
      default: () => null,
    },
    hint: {
      type: [Boolean, String],
      required: false,
      default: false,
    },
    defaultIcon: {
      type: [String, Boolean],
      required: false,
      default: () => 'far fa-paperclip',
    },
    maxSize: {
      type: Number,
      required: false,
      default: () => null,
    },
    // e.g. "environment": phones and the PWA open the camera; desktop browsers ignore it
    // (IOSUITE-7846).
    capture: {
      type: String,
      required: false,
      default: undefined,
    },
  },
  data() {
    return {
      internalValue: this.value,
      internalError: '',
    };
  },
  computed: {
    hasSlotIconLeft() {
      return !!this.$slots.iconLeft;
    },
    hasSlotIconRight() {
      return !!this.$slots.iconRight;
    },
    clearCondition() {
      return this.clearable && (this.hasValue || this.search);
    },
    openFileCondition() {
      return (!this.hasItems && this.multiple) || !this.multiple;
    },
    openFileOnIconCondition() {
      // Single-file inputs: the icon is either "clear" or "open", never both.
      if (!this.multiple) {
        return !this.clearCondition;
      }
      return this.openFileCondition || !this.clearCondition;
    },
    hasError() {
      return !!(this.error || this.internalError);
    },
    errorMessage() {
      return this.internalError || this.error;
    },
    hasList() {
      return this.hasItems && this.multiple;
    },
    buttonId() {
      return `${this.uniqueId}-button`;
    },
    listboxId() {
      return `${this.uniqueId}-listbox`;
    },
    toggleLabel() {
      const base = this.label || 'Dateien';
      if (this.openFileCondition) {
        return `${base} auswählen`;
      }
      if (!this.hasList) {
        return base;
      }

      return this.listActive ? `${base} schließen` : `${base} öffnen`;
    },
    badgeSize() {
      if (this.xSmall) return 28;
      if (this.small) return 36;
      return 50;
    },
    iconRightLabel() {
      if (this.clearCondition) {
        return 'Auswahl löschen';
      }
      if (this.openFileCondition) {
        return this.toggleLabel;
      }

      return this.listActive ? 'Dateiliste schließen' : 'Dateiliste öffnen';
    },
  },
  watch: {
    value(val) {
      this.updateInternalValue(val);
    },
  },
  mounted() {
    // eslint-disable-next-line no-undef
    GlobalEvents.listen(`${this.name}.click`, () => this.$refs.file.click());

    if (this.value) {
      if (!this.value.length) {
        this.itemList = [];
      }

      this.updateInternalValue(this.value);
    }
  },
  destroyed() {
    window.GlobalEvents.destroy(`${this.name}.click`);
  },
  methods: {
    // ClearHook override: BaseSelectMixin reopens dropdown after clear; single-file inputs have no dropdown — skip reopen to avoid stuck --open styling.
    ClearHook({ event }) {
      event.stopPropagation();
      this.search = '';
      this.listActive = false;
      this.internalError = '';
      this.resetFileInput();
      if (this.multiple) {
        this.toggleList();
      }
    },
    // Without this, picking the same file again fires no input event and the choice is lost.
    resetFileInput() {
      if (this.$refs.file) {
        this.$refs.file.value = '';
      }
    },
    visibilityItemIcon(item) {
      return { visibility: item.icon ? 'show' : 'hidden' };
    },
    getRemoveLabel(selection) {
      const item = this.getItem(selection) || {};
      const label = item.label || selection;

      return `Auswahl ${label} entfernen`;
    },
    setSelection(val) {
      const value = val.map((item) => this.internalItems.find((x) => x.value === item).file);

      this.emitUpdate(value);
    },
    selectFile(val) {
      // Clear any previous errors
      this.internalError = '';

      // Check file size if maxSize is specified
      if (this.maxSize) {
        const maxSizeBytes = this.maxSize * 1024; // Convert KB to bytes
        const oversizedFiles = [];
        const invalidFormatFiles = [];

        for (let i = 0; i < val.length; i += 1) {
          // Check file size
          if (val[i].size > maxSizeBytes) {
            oversizedFiles.push(`${val[i].name} (${Math.round(val[i].size / 1024)} KB)`);
          }

          // Check file format if accept is specified
          if (this.accept && !this.isValidFileType(val[i])) {
            invalidFormatFiles.push(val[i].name);
          }
        }

        // Handle file size errors
        if (oversizedFiles.length > 0) {
          const errorMessage = oversizedFiles.length === 1
            ? `Datei ${oversizedFiles[0]} überschreitet die maximale Größe von ${this.maxSize} KB`
            : `${oversizedFiles.length} Dateien überschreiten die maximale Größe von ${this.maxSize} KB: ${oversizedFiles.join(', ')}`;

          this.internalError = errorMessage;
          this.$emit('error', errorMessage);
          return;
        }

        // Handle file format errors
        if (invalidFormatFiles.length > 0) {
          const allowedTypes = this.accept.replace(/\*/g, '').split(',').map((type) => type.trim()).join(', ');
          const errorMessage = invalidFormatFiles.length === 1
            ? `Datei "${invalidFormatFiles[0]}" hat ein ungültiges Format. Erlaubte Formate: ${allowedTypes}`
            : `${invalidFormatFiles.length} Dateien haben ungültige Formate. Erlaubte Formate: ${allowedTypes}`;

          this.internalError = errorMessage;
          this.$emit('error', errorMessage);

          return;
        }
      } else if (this.accept) {
        // Check file format
        const invalidFormatFiles = [];

        for (let i = 0; i < val.length; i += 1) {
          if (!this.isValidFileType(val[i])) {
            invalidFormatFiles.push(val[i].name);
          }
        }

        if (invalidFormatFiles.length > 0) {
          const allowedTypes = this.accept.replace(/\*/g, '').split(',').map((type) => type.trim()).join(', ');
          const errorMessage = invalidFormatFiles.length === 1
            ? `Datei "${invalidFormatFiles[0]}" hat ein ungültiges Format. Erlaubte Formate: ${allowedTypes}`
            : `${invalidFormatFiles.length} Dateien haben ungültige Formate. Erlaubte Formate: ${allowedTypes}`;

          this.internalError = errorMessage;
          this.$emit('error', errorMessage);

          return;
        }
      }

      this.itemList = [];
      for (let i = 0; i < val.length; i += 1) {
        this.itemList.push({
          label: val[i].name,
          value: val[i].name,
          file: val[i],
          url: URL.createObjectURL(val[i]),
        });
      }
      this.emitUpdate(Object.values(val));
      this.updateInternalValue(Object.values(val));
    },
    updateInternalValue(val) {
      let internalValue = [];

      if ((this.multiple && val.length) || (!this.multiple && val)) {
        if (this.multiple) {
          for (let i = 0; i < val.length; i += 1) {
            internalValue.push(val[i].name);
          }
        } else if (typeof val === 'string') {
          // Stored file names are plain strings; indexing them like arrays yields a character.
          internalValue = val;
        } else if (val && val[0]) {
          internalValue = val[0].name;
        } else if (val && val.name) {
          internalValue = val.name;
        } else {
          internalValue = [];
        }
      }

      this.internalValue = internalValue;
    },
    isValidFileType(file) {
      if (!this.accept) return true;

      const acceptedTypes = this.accept.split(',').map((type) => type.trim());

      return acceptedTypes.some((acceptedType) => {
        if (acceptedType.startsWith('.')) {
          // File extension check
          return file.name.toLowerCase().endsWith(acceptedType.toLowerCase());
        } if (acceptedType.includes('*')) {
          // MIME type pattern check (e.g., image/*)
          const mimePattern = acceptedType.replace('*', '.*');
          const regex = new RegExp(`^${mimePattern}$`, 'i');

          return regex.test(file.type);
        }

        // Exact MIME type check
        return file.type.toLowerCase() === acceptedType.toLowerCase();
      });
    },
  },
};
</script>
